import React, { useEffect, useState } from "react";
import { gql } from "@apollo/client";
import { styled, useTheme } from "@material-ui/styles";
import { Theme, TextField, Paper } from "@material-ui/core";
import Content from "../content";
import { useQuery, useMutation } from "@apollo/client";
import Typography from "../typography";
import moment from "moment";
import PlayerEvents from "./PlayerEventsComponent";
import { injectIntl } from "react-intl";
import messages from "./PageDashboard.intl";
import { IStyledArguments } from "../../types/styled-arguments";
import InitialCard from "./InitialCard";
import NotificationsCard from "./NotificationsCard";
import PreSeasonEntriesCard from "./PreSeasonEntriesCard";
import { Redirect } from "@reach/router";
import GreetingHeader from "./GreetingHeader";
import { Hero, Eyebrow, BigTime, Subtitle, Divider } from "./NextEventHero";

const QUERY_PLAYER = gql`
  query playerDetails($id: ID!) {
    player(id: $id) {
      id
      name
      surname
      number
      position
      dateOfBirth
      avatarUrl
      team {
        id
        name
        trainingSetup {
          monday
          tuesday
          wednesday
          thursday
          friday
          saturday
          sunday
        }
        nextTraining {
          trainingAttendance {
            player {
              id
              name
            }
            attendance
            reason
          }
          date
        }
      }
      notes {
        note
        date
      }
      totalGoals
      totalMatches
      reviews {
        data {
          speed
          vision
          dribbling
          shoot
          physical
          leadership
          defense
        }
        date
      }
    }
  }
`;

const ATTEND_TRAINING = gql`
  mutation AttendTraining(
    $id: ID!
    $teamId: ID!
    $date: String!
    $attend: Boolean!
    $reason: String
  ) {
    attendTraining(
      id: $id
      teamId: $teamId
      date: $date
      attend: $attend
      reason: $reason
    )
  }
`;

const dayMapper: any = {
  1: "monday",
  2: "tuesday",
  3: "wednesday",
  4: "thursday",
  5: "friday",
  6: "saturday",
  7: "sunday",
};

const MainContent = styled(Content)<Theme>(({ theme }) => ({
  padding: theme.spacing(2.5),
  paddingBottom: 96,
  background: theme.palette.extra.backgroundGrey,
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2.5),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    paddingBottom: 96,
    gap: theme.spacing(2),
  },
}));

const StatsRow = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  gap: theme.spacing(1.25),
}));

const StatTile = styled("div")(({ theme }: IStyledArguments) => ({
  flex: 1,
  textAlign: "center",
  background: "#ffffff",
  border: `1px solid ${theme.palette.extra.borderColor}`,
  borderRadius: 14,
  padding: theme.spacing(1.75, 0.5),
}));

const StatValue = styled("div")(({ theme }: IStyledArguments) => ({
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 800,
  fontSize: "1.4rem",
  color: theme.palette.text.primary,
  fontVariantNumeric: "tabular-nums",
}));

const StatLabel = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.65rem",
  fontWeight: 600,
  textTransform: "uppercase",
  letterSpacing: 0.4,
  color: theme.palette.text.secondary,
  marginTop: 3,
}));

const AttendancePrompt = styled("div")(({ theme }: IStyledArguments) => ({
  fontSize: "0.8rem",
  fontWeight: 500,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1.25),
}));

const AttendanceButtons = styled("div")(({ theme }: IStyledArguments) => ({
  display: "flex",
  gap: theme.spacing(1.25),
}));

const YesButton = styled("button")<{ $active?: boolean }>(
  ({ theme, $active }: IStyledArguments & { $active?: boolean }) => ({
    flex: 1,
    background: $active ? theme.palette.primary.main : "transparent",
    color: $active ? theme.palette.primary.contrastText : theme.palette.text.primary,
    border: $active ? "none" : `1px solid ${theme.palette.extra.borderColor}`,
    borderRadius: 10,
    padding: theme.spacing(1.6, 0),
    fontFamily: theme.typography.alternateFontFamily,
    fontWeight: 600,
    fontSize: "0.9rem",
    cursor: "pointer",
  })
);

const NoButton = styled("button")<{ $active?: boolean }>(
  ({ theme, $active }: IStyledArguments & { $active?: boolean }) => ({
    flex: 1,
    background: $active ? theme.palette.error.main : "transparent",
    color: $active ? "#ffffff" : theme.palette.text.primary,
    border: $active ? "none" : `1px solid ${theme.palette.extra.borderColor}`,
    borderRadius: 10,
    padding: theme.spacing(1.6, 0),
    fontFamily: theme.typography.alternateFontFamily,
    fontWeight: 600,
    fontSize: "0.9rem",
    cursor: "pointer",
  })
);

const ReasonRow = styled("div")(({ theme }: IStyledArguments) => ({
  marginTop: theme.spacing(2),
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(1.25),
}));

const SendButton = styled("button")(({ theme }: IStyledArguments) => ({
  alignSelf: "flex-start",
  background: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  border: "none",
  borderRadius: 10,
  padding: theme.spacing(1.2, 2.5),
  fontFamily: theme.typography.alternateFontFamily,
  fontWeight: 600,
  fontSize: "0.85rem",
  cursor: "pointer",
  "&:disabled": {
    opacity: 0.5,
    cursor: "not-allowed",
  },
}));

const NoTeamCard = styled(Paper)(({ theme }: IStyledArguments) => ({
  padding: theme.spacing(4),
  background: theme.palette.background.paper,
  borderRadius: 16,
  border: `1px solid ${theme.palette.extra.borderColor}`,
  boxShadow: "0 1px 3px rgba(16, 21, 26, 0.06)",
  textAlign: "center",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(3),
    borderRadius: 14,
  },
}));

interface IPlayerDashboard {
  id: string;
  user: any;
}
const PlayerDashboard = injectIntl<IPlayerDashboard>(({ id, user, intl }) => {
  //TODO: change it to user.playerLink
  const theme = useTheme<Theme>();

  const { data, refetch } = useQuery<any>(QUERY_PLAYER, {
    variables: { id: id ? id : user.playerLink },
  });
  const [player, setPlayer] = useState<any>(user.playerProfile);
  const [nextTraining, setNextTraining] = useState<any>();
  const [trainingTime, setTrainingTime] = useState<any>();
  const [trainingWeekList, setTrainingWeekList] = useState<any>();

  const [attendTraining] = useMutation<any>(ATTEND_TRAINING);
  const [attendTrainingYes, setAttendTrainingYes] = useState<
    boolean | undefined
  >();
  const [absentReason, setAbsentReason] = useState<string>("");
  const [sentAbsentReason, setSendAbsentReason] = useState<boolean>(false);
  const [redirectToCreateLinkPlayer, setRedirectToCreateLinkPlayer] = useState<
    boolean
  >(false);

  const handleAttendTraining = async (nextState: boolean) => {
    await attendTraining({
      variables: {
        id,
        teamId: player.team.id,
        date: moment(nextTraining).format("YYYY-MM-DD"),
        attend: nextState,
        reason: absentReason,
      },
    });
  };

  const handleYes = () => {
    setAttendTrainingYes(true);
    handleAttendTraining(true);
  };

  const handleNo = () => {
    setAttendTrainingYes(false);
  };

  const handleAddReason = (e: any) => {
    setAbsentReason(e.target.value);
  };

  const handleSendAbsentReason = () => {
    setSendAbsentReason(true);
    if (attendTrainingYes !== undefined) {
      handleAttendTraining(attendTrainingYes);
    }
  };

  useEffect(() => {
    if (data && data.player === null) {
      //TODO: redirect to create player
      setRedirectToCreateLinkPlayer(true);
    }
    if (data && data.player) {
      setPlayer(data.player);
    }
    if (data && data.player.team) {
      const trainingSetup = { ...data.player.team.trainingSetup };

      if (trainingSetup) {
        const filtered = Object.keys(trainingSetup).filter((key) => {
          if (key !== "__typename") {
            return trainingSetup[key];
          }
        });

        const trainingList = filtered.map((day) =>
          moment()
            .day(day)
            .isBefore(Date.now(), "day")
            ? moment()
                .day(day)
                .add(7, "day")
                .toDate()
            : moment()
                .day(day)
                .toDate()
        );
        trainingList.sort((a, b) =>
          moment(a).isAfter(moment(b, "day")) ? 1 : -1
        );
        setNextTraining(trainingList[0]);
        const trainingWeekListMapped = trainingList.map((day) =>
          moment(day).date()
        );

        const nextDayTraining = dayMapper[moment(trainingList[0]).isoWeekday()];
        const nextTrainingTime = trainingSetup[nextDayTraining];
        setTrainingTime(nextTrainingTime);

        setTrainingWeekList(trainingWeekListMapped);
      }
      if (data.player.team.nextTraining) {
        const attendance = [
          ...data.player.team.nextTraining.trainingAttendance,
        ];
        const amIInTheList = attendance.filter(
          (element) => element.player.id === id
        );
        if (amIInTheList && amIInTheList.length === 1) {
          setAttendTrainingYes(amIInTheList[0].attendance);
          setAbsentReason(amIInTheList[0].reason);
        }
      }
    }
  }, [data]);

  return (
    <>
      {redirectToCreateLinkPlayer && (
        <Redirect to={`/users/${user.id}/linkplayer`} noThrow />
      )}
      <MainContent container alignItems="stretch" justify="flex-start">
        {player && (
          <>
            <GreetingHeader
              user={user}
              subtitle={
                player.team
                  ? `${intl.formatMessage(messages.player)} · ${player.team.name}`
                  : undefined
              }
            />

            {trainingWeekList && (
              <>
                <PlayerEvents
                  teamId={player.team.id}
                  trainingWeekList={trainingWeekList}
                  user={user}
                />

                <Hero>
                  <Eyebrow>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2.2}>
                      <circle cx="12" cy="12" r="9" />
                      <path d="M12 7v5l3.2 2" />
                    </svg>
                    {intl.formatMessage(messages.nextTrainings)}
                  </Eyebrow>
                  <BigTime>
                    {trainingTime ? trainingTime : "--:--"}
                  </BigTime>
                  <Subtitle>
                    {moment(nextTraining).isSame(Date.now(), "day")
                      ? intl.formatMessage(messages.today)
                      : moment(nextTraining).format("dddd, DD-MM")}
                    {player.team ? ` · ${player.team.name}` : ""}
                  </Subtitle>

                  <Divider />

                  <AttendancePrompt>{intl.formatMessage(messages.willYouAttend)}</AttendancePrompt>
                  <AttendanceButtons>
                    <YesButton
                      type="button"
                      $active={attendTrainingYes === true}
                      onClick={handleYes}
                    >
                      {intl.formatMessage(messages.goingToTraining)}
                    </YesButton>
                    <NoButton
                      type="button"
                      $active={attendTrainingYes === false}
                      onClick={handleNo}
                    >
                      {intl.formatMessage(messages.notGoingToTraining)}
                    </NoButton>
                  </AttendanceButtons>

                  {!sentAbsentReason &&
                    attendTrainingYes !== undefined &&
                    !attendTrainingYes && (
                      <ReasonRow>
                        <TextField
                          placeholder="absent reason"
                          onChange={handleAddReason}
                          value={absentReason}
                          fullWidth
                          variant="outlined"
                        />
                        <SendButton
                          type="button"
                          disabled={absentReason === ""}
                          onClick={handleSendAbsentReason}
                        >
                          {intl.formatMessage(messages.send)}
                        </SendButton>
                      </ReasonRow>
                    )}
                  {sentAbsentReason && (
                    <Typography.Subtitle1
                      style={{
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                        marginTop: 16,
                      }}
                    >
                      {intl.formatMessage(messages.reason)}&nbsp;
                      {absentReason}
                    </Typography.Subtitle1>
                  )}
                </Hero>
              </>
            )}

            {player.totalGoals !== undefined && (
              <StatsRow>
                <StatTile>
                  <StatValue>{player.totalMatches || 0}</StatValue>
                  <StatLabel>{intl.formatMessage(messages.statMatches)}</StatLabel>
                </StatTile>
                <StatTile>
                  <StatValue>{player.totalGoals || 0}</StatValue>
                  <StatLabel>{intl.formatMessage(messages.statGoals)}</StatLabel>
                </StatTile>
                <StatTile>
                  <StatValue style={{ fontSize: "1.05rem" }}>{player.position || "—"}</StatValue>
                  <StatLabel>{player.number ? `#${player.number}` : ""}</StatLabel>
                </StatTile>
              </StatsRow>
            )}

            <NotificationsCard
              userId={user.id.toString()}
              notifications={user.notifications}
              callback={() => refetch()}
            />

            <PreSeasonEntriesCard />

            {user && !user.workspace && <InitialCard user={user} callback={() => {}} />}

            {player && !player.team && (
              <>
                <NoTeamCard elevation={0}>
                  <Typography.H6 style={{ fontWeight: 600, marginBottom: 16 }}>
                    No team yet
                  </Typography.H6>
                  <Typography.Subtitle1 style={{ color: theme.palette.text.secondary }}>
                    {`Your coach will assign you to a new team soon`}
                  </Typography.Subtitle1>
                </NoTeamCard>
                <NoTeamCard elevation={0}>
                  <Typography.H6 style={{ fontWeight: 700 }}>
                    No events yet
                  </Typography.H6>
                </NoTeamCard>
              </>
            )}
          </>
        )}
      </MainContent>
    </>
  );
});

export default PlayerDashboard;
